import { createBrowserRouter, Navigate } from 'react-router-dom';
import App from './app';
import { Clients } from '../pages/clients';
import { SettingsLayout } from './ui/settings-layout';
import { MainLayout } from './ui/main-layout';
import { CsrReport } from '../pages/csr-report';
import { MissingAppts } from '../pages/missing-appts';
import { AttemptedLeads } from '../pages/attempted-leads';
import { TwoHourSla, TwoHourSlaNW } from '../pages/two-hour-sla';
import { Client, ClientOverview } from '../pages/client';
import { Soa } from '../pages/client/ui/soa/soa';
import { Notes } from '../pages/client/ui/notes';
import { Calls } from '../pages/client/ui/calls';
import { Enrollments } from '../pages/client/ui/enrollments';
import { ClientNextActions } from '../pages/client/ui/client-next-actions';
import { Emails } from '../pages/client/ui/emails';
import { Files } from '../pages/files';
import { Dashboard } from '../pages/dashboard';
import { Contacted } from '../pages/dashboard/tabs/contacted';
import { Goals } from '../pages/dashboard/tabs/goals';
import { Agents } from '../pages/agents';
import { Profile } from '../pages/profile';
import { UrgentCalls } from '../pages/urgent-calls';
import { Integrations } from '../pages/integrations';
import { PlanCoverage } from '../pages/plan-coverage';
import { EmailTemplates } from '../pages/email-templates';
import { Tags } from '../pages/tags';
import { NextActions } from '../pages/next-actions';
import { RoundRobin } from '../pages/round-robin';
import { RoundRobinAgents } from '../pages/round-robin/ui/round-robin-agents';
import { RoundRobinTasks } from '../pages/round-robin/ui/round-robin-tasks';
import { RoundRobinState } from '../pages/round-robin/ui/round-robin-state';
import { GmailVerificationCode } from '../pages/integrations/gmail-verification-code';
import { PartnerLeads } from '../pages/partner-leads/partner-leads';
import { Inbox } from '../pages/dashboard/tabs/inbox';
import {
  SameDayFollowUps,
  TodayFollowUps,
  FutureFollowUps,
  MissedApps,
  OutdatedFollowUps
} from '../pages/dashboard/tabs/follow-ups';
import { FutureSalesQualified, TodaySalesQualified, OutdatedSalesQualified } from '../pages/dashboard/tabs/sales-qualified';
import { SalesManager } from '../pages/sales-manager';
import { DirectMail } from '../pages/direct-mail';
import { ConversionSync } from '../pages/conversion-sync';
import { Qa } from '../pages/qa';
import { Calendar } from '../pages/calendar';

const basePath = '';

const routes = {
  clients: 'clients',
  calendar: 'calendar',
  conversionSync: 'conversion-sync',
  client: 'client',
  dashboard: 'dashboard',
  tags: 'tags',
  agents: 'agents',
  files: 'files',
  planCoverage: 'plan-coverage',
  attemptedLeads: 'attempted-leads',
  integrations: 'integrations',
  urgentCalls: 'urgent-calls',
  twoHourSla: '2-hour-sla',
  missingApps: 'missing-apps',
  emailTemplates: 'email-templates',
  salesManager: 'sales-manager',
  csrTrackingReport: 'csr-report',
  actions: 'next-actions',
  profile: 'profile',
  settings: 'settings',
  roundRobin: 'round-robin',
  twoHourSlaNW: '2-hour-sla-nw',
  partnerLeads: 'partner-leads',
  directMail: 'direct-mail',
  qa: 'qa',
};

export const getRoute = (key: keyof typeof routes): string =>
  basePath + routes[key];

export const router = createBrowserRouter([
  {
    path: basePath,
    element: <App />,
    // ErrorBoundary
    children: [
      {
        path: '*',
        element: <MainLayout />,
        children: [
          {
            path: routes.clients,
            element: <Clients />,
          },
          {
            path: routes.qa,
            element: <Qa />,
          },
          {
            path: routes.calendar,
            element: <Calendar />,
          },
          {
            path: routes.partnerLeads + '/:partner?',
            element: <PartnerLeads />,
          },
          {
            path: routes.directMail,
            element: <DirectMail />,
          },
          {
            path: routes.conversionSync,
            element: <ConversionSync />,
          },
          {
            path: routes.missingApps,
            element: <MissingAppts />,
          },
          {
            path: routes.csrTrackingReport,
            element: <CsrReport />,
          },
          {
            path: routes.attemptedLeads,
            element: <AttemptedLeads />,
          },
          {
            path: routes.twoHourSla,
            element: <TwoHourSla />,
          },
          {
            path: routes.twoHourSlaNW,
            element: <TwoHourSlaNW />,
          },
          {
            path: routes.actions,
            element: <NextActions />,
          },
          {
            path: routes.files,
            element: <Files />,
          },
          {
            path: routes.agents,
            element: <Agents />,
          },
          {
            path: routes.urgentCalls,
            element: <UrgentCalls />,
          },
          {
            path: routes.salesManager,
            element: <SalesManager />
          },
          {
            path: routes.roundRobin,
            element: <RoundRobin />,
            children: [
              {
                path: 'agents',
                element: <RoundRobinAgents />
              },
              {
                path: 'tasks',
                element: <RoundRobinTasks />
              },
              {
                path: 'state',
                element: <RoundRobinState />
              },
              {
                path: '',
                element: <Navigate to={`/${routes.roundRobin}/agents`} replace={true} />,
              },
            ]
          },
          {
            path: routes.dashboard,
            element: <Dashboard />,
            children: [
              {
                path: 'inbox/:agentId?',
                element: <Inbox />
              },
              {
                path: 'today-sql/:agentId?',
                element: <TodaySalesQualified />
              },
              {
                path: 'future-sql/:agentId?',
                element: <FutureSalesQualified />
              },
              {
                path: 'today-follow-ups/:agentId?',
                element: <TodayFollowUps />
              },
              {
                path: 'outdated-follow-ups/:agentId',
                element: <OutdatedFollowUps />
              },
              {
                path: 'outdated-sql/:agentId',
                element: <OutdatedSalesQualified />
              },
              {
                path: 'same-day/:agentId?',
                element: <SameDayFollowUps />
              },
              {
                path: 'future-follow-ups/:agentId?',
                element: <FutureFollowUps />
              },
              {
                path: 'missed-apps/:agentId?',
                element: <MissedApps />
              },
              {
                path: 'contacted/:agentId?',
                element: <Contacted />
              },
              {
                path: 'goals/:agentId?',
                element: <Goals />
              },
              {
                path: '',
                element: <Navigate to={`/${routes.dashboard}/today-sql`} replace={true} />,
              },
            ]
          },
          {
            path: routes.client + '/:id',
            element: <Client />,
            children: [
              {
                path: 'enrollments',
                element: <Enrollments />
              },
              {
                path: 'soa',
                element: <Soa />
              },
              {
                path: 'actions',
                element: <ClientNextActions />
              },
              {
                path: 'notes',
                element: <Notes />
              },
              {
                path: 'calls',
                element: <Calls />
              },
              {
                path: 'emails',
                element: <Emails />
              },
              {
                path: '*',
                element: <ClientOverview />
              }
            ]
          },
          {
            path: '*',
            element: <Navigate to={routes.dashboard} replace={true} />,
          },
        ]
      },
      {
        path: 'settings',
        element: <SettingsLayout />,
        children: [
          {
            path: routes.profile,
            element: <Profile />,
          },
          {
            path: routes.integrations,
            element: <Integrations />,
          },
          {
            path: routes.planCoverage,
            element: <PlanCoverage />,
          },
          {
            path: routes.emailTemplates,
            element: <EmailTemplates />,
          },
          {
            path: routes.tags,
            element: <Tags />,
          },
        ]
      },
      {
        path: routes.integrations + '/gmail',
        element: <GmailVerificationCode />,
      },
      {
        path: '',
        element: <Navigate to={routes.dashboard} replace={true} />,
      },
      {
        path: '*',
        element: <Navigate to={routes.dashboard} replace={true} />,
      },
    ],
  },
  {
    path: '',
    element: <Navigate to={getRoute('dashboard')} replace={true} />,
  },
  {
    path: '*',
    element: <Navigate to={getRoute('dashboard')} replace={true} />,
  },
]);
